/**
 * Chat section memory is written by the platform, not by the providers: a provider ends its answer with blocks like
 *
 *   <codvero-memory layer="chat" kind="did">
 *   - Installed 16 coding skills in /workspace/.agents/skills
 *   </codvero-memory>
 *
 * and the platform adds them, in turn, to the right file (several providers answer the same conversation at once;
 * each rewriting the file erased the others). The blocks are taken out of the answer people read.
 */
export type MemoryLayer = "user" | "space" | "chat";
export type MemoryKind = "did" | "rule" | "note";
export interface MemoryBlock {
  layer: MemoryLayer;
  kind: MemoryKind;
  /** the lines to keep, one fact each */
  lines: string[];
}

const BLOCK = /<codvero-memory\b([^>]*)>([\s\S]*?)<\/codvero-memory>/gi;
const attr = (attrs: string, name: string) => new RegExp(`${name}\\s*=\\s*["']?([a-z]+)`, "i").exec(attrs)?.[1]?.toLowerCase();

/** The answer without its memory blocks (an unfinished block while streaming is hidden too), and the blocks. */
export function splitMemoryBlocks(text: string): { text: string; blocks: MemoryBlock[] } {
  const blocks: MemoryBlock[] = [];
  let out = text.replace(BLOCK, (_m, attrs: string, body: string) => {
    const layer = attr(attrs, "layer");
    const kind = attr(attrs, "kind");
    const lines = body
      .split("\n")
      .map((l) => l.trim().replace(/^[-*•]\s*/, "").trim())
      .filter(Boolean)
      .slice(0, 40)
      .map((l) => l.slice(0, 600));
    if (lines.length) {
      blocks.push({
        layer: layer === "user" || layer === "space" ? layer : "chat",
        kind: kind === "rule" || kind === "note" ? kind : layer === "user" || layer === "space" ? "note" : "did",
        lines,
      });
    }
    return "";
  });
  const open = out.search(/<codvero-memory\b/i);
  if (open >= 0) out = out.slice(0, open);
  return { text: out.replace(/\n{3,}/g, "\n\n").trimEnd(), blocks };
}

/** The heading a kind of entry is kept under. */
export const MEMORY_SECTION: Record<MemoryKind, string> = { did: "## What we did", rule: "## Rules and decisions", note: "## Notes" };

/**
 * A memory file with new entries added under their section (made at the end when missing), each entry as one dated
 * line naming who saved it. Nothing already there is changed; an entry already present is not added again.
 */
export function addMemoryEntries(content: string, kind: MemoryKind, lines: string[], stamp: string): string {
  const heading = MEMORY_SECTION[kind];
  const have = new Set(content.split("\n").map((l) => l.replace(/^- (\[[^\]]*\] )?/, "").trim()));
  const fresh = lines.filter((l) => !have.has(l.trim()));
  if (!fresh.length) return content;
  const entries = fresh.map((l) => `- [${stamp}] ${l}`);
  const rows = content.replace(/\s+$/, "").split("\n");
  const at = rows.findIndex((r) => r.trim() === heading);
  if (at < 0) return `${rows.join("\n").trim() ? `${rows.join("\n")}\n\n` : ""}${heading}\n${entries.join("\n")}\n`;
  let end = at + 1;
  while (end < rows.length && !/^#{1,2} /.test(rows[end]!)) end++;
  while (end > at + 1 && !rows[end - 1]!.trim()) end--;
  rows.splice(end, 0, ...entries);
  return `${rows.join("\n")}\n`;
}
